import React from 'react';
import { money, pctLabel, priceChange, unitPrice } from './invoiceStatus';

function fmt(value, unit) {
  const p = unitPrice(value, unit);
  return `${money(p.value)}${p.unit ? `/${p.unit}` : ''}`;
}

/** One row per costed item on the invoice: what its price is now and what this invoice says. */
export function priceRows(invoice, items) {
  const seen = new Set();
  const rows = [];
  for (const ln of invoice?.lines || []) {
    if (!ln.item_id || ln.not_costed || seen.has(ln.item_id)) continue;
    seen.add(ln.item_id);
    const it = items[ln.item_id] || {};
    rows.push({ itemId: ln.item_id, name: it.name || ln.raw_desc, oldPrice: it.current_price, oldUnit: it.current_unit,
      // recorded_*: what approval will write (pack price per g / ml), from the API; else the printed price.
      newPrice: ln.recorded_unit_price ?? ln.unit_price, newUnit: ln.recorded_unit ?? ln.unit, raw: ln.raw_desc,
      history: !!ln.history_only });
  }
  return rows;
}

/** After approval: the price this invoice recorded (sparkline point on its date) vs before, and today's price. */
export function resultRows(invoice, items, before = {}) {
  return priceRows(invoice, items).map((r) => {
    const it = items[r.itemId] || {};
    const spark = it.sparkline || [];
    const at = spark.map((p, i) => [p, i]).filter(([p]) => p.date === invoice.date).pop();
    const recorded = at ? at[0].unit_price : null;
    const prev = at && at[1] > 0 ? spark[at[1] - 1].unit_price : null;
    const old = before[r.itemId];
    const newer = recorded !== null && it.current_price != null && String(it.current_price) !== String(recorded);
    return { ...r, oldPrice: old ? old.price : prev, oldUnit: old ? old.unit : it.current_unit,
      newPrice: recorded ?? it.current_price, newUnit: it.current_unit,
      note: newer ? `Today's price stays ${fmt(it.current_price, it.current_unit)}: a newer invoice sets it.` : null };
  });
}

export default function PriceChangeTable({ rows, testid = 'price-change-table', afterOnly = false }) {
  if (!rows.length) return <p className="text-sm text-ink-muted" data-testid={`${testid}-empty`}>No catalogue prices change.</p>;
  return (
    <ul className="divide-y divide-surface-border rounded-xl border border-surface-border max-h-72 overflow-auto" data-testid={testid}>
      {rows.map((r) => {
        const o = unitPrice(r.oldPrice, r.oldUnit);
        const n = unitPrice(r.newPrice, r.newUnit);
        const ch = priceChange(o.value, o.unit, n.value, n.unit);
        const up = ch.pct !== null && ch.pct > 0;
        return (
          <li key={r.itemId} className="px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`${testid}-row-${r.itemId}`}>
            <span className="flex-1 min-w-[8rem] text-ink font-medium">{r.name}</span>
            <span className="font-mono text-sm text-ink-muted">
              {!afterOnly && (r.oldPrice != null ? fmt(r.oldPrice, r.oldUnit) : 'no price yet')}
              {!afterOnly && ' → '}
              <span className="text-ink">{fmt(r.newPrice, r.newUnit)}</span>
            </span>
            <span className={`text-sm font-medium w-20 text-right ${up ? 'text-danger' : 'text-primary-700'}`}>
              {afterOnly ? '' : ch.pct !== null ? pctLabel(ch.pct) : ch.comparable ? '' : 'per pack'}
            </span>
            {r.note && <span className="basis-full text-xs text-ink-muted" data-testid={`${testid}-note-${r.itemId}`}>{r.note}</span>}
          </li>
        );
      })}
    </ul>
  );
}
